import React, { useRef, useState } from 'react'
import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import { useLocation, useNavigate } from 'react-router-dom'
import Box from '@mui/system/Box'
import Typography from '@mui/material/Typography'
import Icon from '@mui/material/Icon'
import StatusBar from 'components/reusable/statusBar/AdxStatusBar'
import ClaimRequest from '../module/ClaimRequest'
import { useMutation, useQueryClient } from 'react-query'
import { postApproveReport } from 'services/api/modules/claim/approval-center/report'
import reportStatusConverter from 'module/claim/services/helper/reportStatusConverter'
import ClaimRejectForm from 'pages/approval-center/components/claim/ClaimRejectForm'
import AdxInfoModal from 'components/reusable/modal/AdxInfoModal'
import defaultImage from 'assets/images/default_avatar.png'
import AdxButton from 'components/custom/AdxButton'

const ClaimApprovalRequest = ({ data }) => {
  const { id, staff_name, staff_img, title, amount_total, amount_payable, report_status } = data

  // formik ref
  const formikRef = useRef()

  // state to differentiate type of tab: pending, upcoming or responded
  const { state } = useLocation()
  const navigate = useNavigate()
  const pendingTab = state.backgroundLocation.pathname.includes('approval-center/claim/pending')
  const reimbursementTab = state.backgroundLocation.pathname.split('/').pop() === 'reimbursement'
  const respondedTab = state.backgroundLocation.pathname.split('/').pop() === 'responded'
  const upcomingTab = state.backgroundLocation.pathname.split('/').pop() === 'upcoming'

  // state of the text amount for the modal footer specifically needs for claim module
  const [amount, setAmount] = useState(data?.amount)

  // state of submit button
  const [submitDisabled, setSubmitDisable] = useState(true)

  // state of the modal, always open. It will rendered using routing
  const [isModalOpen, setModalOpen] = useState(true)
  const handleClose = () => {
    navigate(state.backgroundLocation)
  }

  // state of submit modal
  const [isSubmitModalOpen, setSubmitModalOpen] = useState(false)
  const handleSubmitOpen = () => setSubmitModalOpen(true)
  const handleSubmitClose = () => setSubmitModalOpen(false)

  // state of the reject modal
  const [isRejectModalOpen, setRejectModalOpen] = useState(false)
  const handleRejectClose = () => {
    setRejectModalOpen(false)
  }
  const handleReject = () => setRejectModalOpen(true)

  // avatar microcomponent
  const headerAvatar = (mobile) => (
    <Box
      as="img"
      sx={{
        display: mobile ? { xs: 'block', lg: 'none' } : { xs: 'none', lg: 'block' },
        height: 50,
        width: 50,
        bgcolor: 'gray-background.main',
        objectFit: 'cover',
        borderRadius: '50%',
        border: '3px solid',
        borderColor: 'white.main',
      }}
      alt={staff_name}
      src={staff_img || defaultImage}
    />
  )

  // header of the modal
  const customHeader = (
    <Box display="flex" justifyContent="space-between" alignItems="center" padding={3}>
      <Box display="flex" alignItems="center" columnGap={2}>
        {headerAvatar(true)}
        <Box
          display="flex"
          alignItems={{ xs: 'flex-start', lg: 'center' }}
          columnGap={1.5}
          flexDirection={{ xs: 'column', lg: 'row' }}
          rowGap={1}
        >
          <Box
            display="flex"
            alignItems={{ xs: 'flex-start', lg: 'center' }}
            columnGap={1.5}
            flexDirection={{ xs: 'column', lg: 'row' }}
          >
            {headerAvatar(false)}
            <Typography variant="h4" fontWeight="bold" color="dark-text.main" noWrap>
              {title}
            </Typography>
            <Icon
              sx={{
                display: { xs: 'none', lg: 'block' },
                fontSize: 7,
                color: 'alternate.main',
              }}
            >
              circle_rounded
            </Icon>
            <Typography noWrap variant="h4" fontWeight="bold" color="alternate.main">
              {`RM ${parseFloat(amount_payable === 0 ? amount_total : amount_payable).toFixed(2)}`}
            </Typography>
          </Box>
          <StatusBar status={reportStatusConverter(report_status?.name || 'Unknown')} />
        </Box>
      </Box>
      <Box onClick={handleClose} sx={{ cursor: 'pointer' }}>
        <Icon sx={{ color: 'dark-text.main' }}>close_rounded</Icon>
      </Box>
    </Box>
  )

  // approve API
  const queryClient = useQueryClient()
  const {
    isLoading: approveIsLoading,
    error: approveError,
    isSuccess: approveIsSuccess,
    mutate: mutateApprove,
  } = useMutation((submissionValues) => postApproveReport(submissionValues, id), {
    onSuccess: async (res) => {
      queryClient.invalidateQueries('currentRequest')
      queryClient.invalidateQueries('respondedRequest')
      queryClient.invalidateQueries('reimburseRequest')
      queryClient.invalidateQueries(['singleApprovalReport', id])
      handleSubmitClose()
      handleClose()
    },
    onError: async (e) => console.log(e),
  })

  // update values from formik
  const handleApproveSubmit = () => {
    const expenseArray = formikRef?.current?.values?.expenseArray || []

    const submitValues = {
      action: 1,
      expenses: expenseArray.map((list) => ({
        id: list.id,
        amount: Number(list.amount),
        amount_payable: Number(list.amount_payable),
      })),
    }

    mutateApprove(submitValues)
  }

  return (
    <>
      <AdxFormModal
        open={isModalOpen}
        handleClose={handleClose}
        handleAction={() => alert('Action button pressed')}
        disableAutoFocus
        onClose={handleClose}
        customHeader={customHeader}
        enableFooter={pendingTab || respondedTab}
        footerprops={
          (pendingTab || respondedTab) && (
            <Box
              display="flex"
              flexDirection={{ xs: 'column', lg: 'row' }}
              justifyContent="space-between"
              alignItems="center"
              width={1}
              bgcolor="primary.light"
              padding={3}
              rowGap={1.5}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                columnGap={2}
                width={{ xs: 1, lg: 'auto' }}
              >
                <Typography variant="h4">Total reimbursement</Typography>
                <Typography variant="h4">{amount}</Typography>
              </Box>
              {pendingTab && (
                <Box
                  display="flex"
                  columnGap={1.5}
                  alignItems="center"
                  width={{ xs: 1, lg: 'auto' }}
                >
                  <AdxButton
                    variant={'outlined'}
                    onClick={handleReject}
                    sx={{
                      width: '150px',
                    }}
                  >
                    Reject
                  </AdxButton>
                  <AdxButton
                    variant={'contained'}
                    onClick={handleSubmitOpen}
                    disableElevation
                    disabled={submitDisabled}
                    sx={{
                      width: '150px',
                    }}
                  >
                    Submit approval
                  </AdxButton>
                </Box>
              )}
            </Box>
          )
        }
      >
        <Box>
          <ClaimRequest
            reportData={data}
            editable={pendingTab}
            setAmount={setAmount}
            mutateApprove={mutateApprove}
            setSubmitDisable={setSubmitDisable}
            tab={{ pendingTab, reimbursementTab, respondedTab, upcomingTab }}
            ref={formikRef}
          />
        </Box>
      </AdxFormModal>

      {/* Reject modal */}
      <AdxFormModal
        title={`Reject ${title}?`}
        subtitle={null}
        open={isRejectModalOpen}
        handleClose={handleRejectClose}
        handleAction={() => alert('Action button pressed')}
        disableAutoFocus
        onClose={(_, reason) => {
          if (reason !== 'backdropClick') {
            handleClose()
          }
        }}
      >
        <ClaimRejectForm
          mutateApprove={mutateApprove}
          approveIsLoading={approveIsLoading}
          handleRejectClose={handleRejectClose}
        />
      </AdxFormModal>

      {/* submit moda */}
      <AdxInfoModal
        open={isSubmitModalOpen}
        onClose={handleSubmitClose}
        handleClose={handleSubmitClose}
        iconColor="alternate.main"
        title="Confirm submission?"
        subtitle={null}
        footerButtons
        firstBtnLabel={'Approve report'}
        firstBtnHandle={handleApproveSubmit}
        secondBtnLabel={'Back'}
        secondBtnHandle={handleSubmitClose}
      >
        {/* <pre>{JSON.stringify(values, null, 4)}</pre> */}
      </AdxInfoModal>
    </>
  )
}

export default ClaimApprovalRequest
